import React from 'react';

/**
 * Фаза обработки: на каком этапе задача и сколько ещё ждать.
 *
 * Этапы названы тем, что происходит с записью, а не именами сервисов: человеку
 * нужно понимать, докуда дошло, а не через какой конвейер это идёт.
 */

const STAGE_TITLE = {
  'Скачивание': 'Скачиваем запись',
  'Загрузка': 'Загружаем запись',
  'Очередь': 'Ждём свободный слот',
  'Распознавание': 'Распознаём речь',
  'Имена участников': 'Разбираем, кто говорит',
  'Разбор по каналам': 'Разбираем каналы',
};

function steps(byChannels, kind) {
  // У ссылки запись сначала надо забрать с хостинга — это самая долгая часть,
  // и «Загрузка» с «Очередью» там были бы неправдой: файл человек не отдавал.
  if (kind === 'link') return ['Скачивание', 'Очередь', 'Распознавание'];
  return ['Загрузка', 'Очередь', 'Распознавание',
    byChannels ? 'Разбор по каналам' : 'Имена участников'];
}

function formatEta(seconds) {
  if (typeof seconds !== 'number' || seconds <= 0) return null;
  const minutes = Math.round(seconds / 60);
  return minutes >= 1 ? `~${minutes} мин` : `~${seconds} с`;
}

// Без вкладки запись доделывает завершитель bit-history, и результат он кладёт
// в Историю. Ссылку под него не ставят, а при выключенной Истории класть
// некуда — в обоих случаях результат живёт только в открытой вкладке. До
// 14.10.2026 экран и тут обещал «можно закрыть»: минуты списывались, записи не было.
function closeHint(kind, historyEnabled) {
  if (kind === 'link') return 'Не закрывайте вкладку — запись по ссылке доделывается только в ней.';
  if (!historyEnabled) return 'Не закрывайте вкладку — История выключена, результат останется только здесь.';
  return 'Вкладку можно закрыть — запись доделается сама и будет ждать в Истории.';
}

const ProcessingState = ({ fileName, stage, progress, etaSeconds, byChannels, kind,
                           historyEnabled = true, onCancel, cancelling = false }) => {
  const list = steps(byChannels, kind);
  const current = list.indexOf(stage);
  const eta = formatEta(etaSeconds);

  return (
    <div className="flex flex-col gap-3" role="region" aria-label="Ход обработки записи">
      {/* Верстка по «Бумаге», артборд «Распознавание», состояние PROCESSING:
          карточка хода сверху, шаги — списком под ней. */}
      <div className="bg-paper-card rounded-card px-5 pt-5 pb-5 md:px-[30px] md:pt-[26px] md:pb-[22px] flex items-end justify-between gap-6 flex-wrap shrink-0">
        <div className="min-w-0">
          <div className="text-[12px] text-paper-ink-3">Идёт обработка</div>
          {/* aria-live — чтобы смену этапа было слышно: без неё экран для
              незрячего человека просто замолкает на всё время работы. */}
          <div aria-live="polite"
               className="mt-2 font-display text-[28px] md:text-[36px] font-extrabold leading-none tracking-[-0.035em] text-paper-ink">
            {STAGE_TITLE[stage] || stage}
          </div>
          <div className="mt-2 truncate font-mono text-[12px] text-paper-ink-3">{fileName}</div>
        </div>

        <div className="w-full md:w-[300px] max-w-full">
          {/* Пока задача в очереди, честной оценки нет — показываем только
              процент: выдуманное «осталось 5 минут» злит сильнее, чем его
              отсутствие.

              🔴 Номер этапа — не украшение. Полоса считает текущий этап, а не
              всю задачу: после скачивания она сбрасывается и идёт заново, и без
              подписи это читается как откат уже сделанной работы. */}
          <div className="mb-2 flex justify-between font-mono text-[12px] text-paper-ink-3">
            <span>
              {[current >= 0 ? `этап ${current + 1} из ${list.length}` : null,
                eta ? `осталось ${eta}` : null].filter(Boolean).join(' · ')}
            </span>
            <span>{`${progress}%`}</span>
          </div>
          <div className="h-[6px] rounded-[3px] bg-paper-bg overflow-hidden"
               role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}
               aria-label={`${STAGE_TITLE[stage] || stage}: ${progress}%`}>
            <div className="h-full bg-paper-accent transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="bg-paper-card rounded-card px-[10px] py-2">
        <ul>
          {list.map((name, index) => {
            const state = index < current ? 'done' : index === current ? 'now' : 'pending';
            return (
              <li key={name} data-state={state}
                  className={`flex items-center gap-[14px] rounded-control px-3 py-[13px] text-[14px] ${
                    state === 'pending' ? 'text-paper-hint' : 'text-paper-ink'}`}>
                <span className={`w-[10px] h-[10px] rounded-full shrink-0 ${
                  state === 'done' ? 'bg-paper-ink'
                    : state === 'now' ? 'bg-paper-accent animate-pulse'
                      : 'bg-paper-track'}`} />
                {name}
                {state === 'done' && (
                  <span className="ml-auto text-[13px] text-paper-ink-3">готово</span>
                )}
              </li>
            );
          })}
        </ul>

        <div className="mt-1 flex items-center justify-between gap-4 border-t border-paper-line px-3 pt-[14px] pb-[10px] text-[13px] text-paper-ink-3">
          <span>{closeHint(kind, historyEnabled)}</span>
          {/* Отмена ходит на сервер снимать задачу — пока ответа нет, кнопка
              говорит об этом и не принимает второй клик. */}
          <button type="button" onClick={onCancel} disabled={cancelling}
                  className="shrink-0 rounded-chip bg-paper-bg px-[14px] py-2 text-[13px] font-medium text-paper-ink hover:bg-paper-panel disabled:text-paper-hint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-accent">
            {cancelling ? 'Отменяем…' : 'Отменить'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProcessingState;
